import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Location, Warehouse } from "@/data/mockWarehousesLocations";
import { Badge } from "@/components/ui/badge";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";

interface LocationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location | null;
  warehouseCode: string;
  warehouses: Warehouse[];
  existingLocations: Location[];
  onSave: (location: Location) => void;
}

export function LocationFormDialog({ 
  open, 
  onOpenChange, 
  location, 
  warehouseCode, 
  warehouses,
  existingLocations,
  onSave 
}: LocationFormDialogProps) {
  const hierarchyConfig = useWarehouseHierarchyConfig();
  
  const [formData, setFormData] = useState<Partial<Location>>({
    locationCode: "",
    warehouseCode: warehouseCode,
    hierarchyData: {},
    locationType: "Storage",
    capacity: undefined,
    currentStock: 0,
    utilizationPercent: 0,
    pickingSequence: undefined,
    active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate location code based on hierarchy
  const autoGeneratedCode = useMemo(() => {
    const parts: { [key: number]: string } = formData.hierarchyData || {};
    return hierarchyConfig.generateLocationCode(parts);
  }, [formData.hierarchyData, hierarchyConfig]);

  useEffect(() => {
    if (location) {
      // Convert legacy fields to hierarchyData if needed
      const hierarchyData = location.hierarchyData || {};
      if (!location.hierarchyData) {
        // Map legacy fields to hierarchy levels
        // Level 1 = Warehouse (handled separately), Level 2 = Aisle, Level 3 = Rack, Level 4 = Shelf, Level 5 = Bin
        if (location.aisle) hierarchyData[2] = location.aisle;
        if (location.rack) hierarchyData[3] = location.rack;
        if (location.shelf) hierarchyData[4] = location.shelf;
        if (location.bin) hierarchyData[5] = location.bin;
      }
      setFormData({ ...location, hierarchyData });
    } else {
      setFormData({
        locationCode: "",
        warehouseCode: warehouseCode,
        hierarchyData: {},
        locationType: "Storage",
        capacity: undefined,
        currentStock: 0,
        utilizationPercent: 0,
        pickingSequence: undefined,
        active: true,
      });
    }
    setErrors({});
  }, [location, warehouseCode, open]);

  // Update location code when hierarchy changes (only for new locations)
  useEffect(() => {
    if (!location && autoGeneratedCode) {
      setFormData(prev => ({ ...prev, locationCode: autoGeneratedCode }));
    }
  }, [autoGeneratedCode, location]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.warehouseCode) {
      newErrors.warehouseCode = "Warehouse is required";
    }

    if (!formData.locationCode?.trim()) {
      newErrors.locationCode = "Location code is required";
    } else if (!location && existingLocations.some(l => 
      l.locationCode.toLowerCase() === formData.locationCode!.toLowerCase() && 
      l.warehouseCode === formData.warehouseCode
    )) {
      newErrors.locationCode = "This location code already exists in this warehouse";
    }

    if (!formData.locationType) {
      newErrors.locationType = "Location type is required";
    }

    if (formData.pickingSequence !== undefined && formData.pickingSequence < 0) {
      newErrors.pickingSequence = "Picking sequence must be 0 or greater";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const capacity = formData.capacity || 0;
    const currentStock = formData.currentStock || 0;
    const utilizationPercent = capacity > 0 ? Math.round((currentStock / capacity) * 100) : 0;

    // Map hierarchyData back to legacy fields for backward compatibility
    // Level 1 = Warehouse (handled separately), Level 2 = Aisle, Level 3 = Rack, Level 4 = Shelf, Level 5 = Bin
    // Include the prefix so the saved values match what's shown in the location code
    const hierarchyData = formData.hierarchyData || {};
    const getValueWithPrefix = (levelNumber: number): string | undefined => {
      const value = hierarchyData[levelNumber];
      if (!value) return undefined;
      const level = hierarchyConfig.activeLevels.find(l => l.level === levelNumber);
      const prefix = level?.codePrefix?.trim() || "";
      return prefix ? `${prefix}${value}` : value;
    };
    const aisle = getValueWithPrefix(2);
    const rack = getValueWithPrefix(3);
    const shelf = getValueWithPrefix(4);
    const bin = getValueWithPrefix(5);

    onSave({
      locationCode: formData.locationCode!,
      warehouseCode: formData.warehouseCode!,
      hierarchyData: formData.hierarchyData,
      aisle,
      rack,
      shelf,
      bin,
      locationType: formData.locationType as Location["locationType"],
      capacity: formData.capacity,
      currentStock: currentStock,
      utilizationPercent,
      pickingSequence: formData.pickingSequence,
      active: formData.active !== false,
    });
  };

  const handleHierarchyChange = (level: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      hierarchyData: {
        ...prev.hierarchyData,
        [level]: value.toUpperCase(),
      },
    }));
  };

  const isEditMode = !!location;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Location" : "Add Location"}</DialogTitle>
          <DialogDescription>
            {isEditMode ? "Update location details" : "Create a new location with flexible hierarchy"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warehouse */}
          <div className="space-y-2">
            <Label htmlFor="warehouse">
              Warehouse <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.warehouseCode}
              onValueChange={(value) => setFormData({ ...formData, warehouseCode: value })}
              disabled={isEditMode}
            >
              <SelectTrigger className={errors.warehouseCode ? "border-destructive" : ""}>
                <SelectValue placeholder="Select warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.code} value={warehouse.code}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.warehouseCode && <p className="text-sm text-destructive">{errors.warehouseCode}</p>}
          </div>

          {/* Hierarchy Section */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Location Hierarchy (Optional)</Label>
              {!isEditMode && autoGeneratedCode && (
                <Badge variant="outline" className="font-mono">{autoGeneratedCode}</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Define the sub-location within the selected warehouse. Fill in any combination of levels below.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {hierarchyConfig.activeLevels
                .filter(level => level.level > 1) // Skip level 1 (warehouse level)
                .map((level, index) => {
                const value = formData.hierarchyData?.[level.level] || "";
                const isFirstLevel = index === 0;
                const previousLevelNumber = level.level - 1;
                const isPreviousLevelFilled = previousLevelNumber <= 1 || formData.hierarchyData?.[previousLevelNumber];

                return (
                  <div key={level.level} className="space-y-2">
                    <Label htmlFor={`level${level.level}`}>
                      {level.displayName}
                    </Label>
                    <Input
                      id={`level${level.level}`}
                      value={value}
                      onChange={(e) => handleHierarchyChange(level.level, e.target.value)}
                      placeholder={`e.g., ${level.codePrefix}01`}
                      disabled={isEditMode || (!isFirstLevel && !isPreviousLevelFilled)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Location Code (manual override or display) */}
          <div className="space-y-2">
            <Label htmlFor="locationCode">
              Location Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="locationCode"
              value={formData.locationCode}
              onChange={(e) => setFormData({ ...formData, locationCode: e.target.value.toUpperCase() })}
              placeholder="Auto-generated or enter manually"
              disabled={isEditMode}
              className={errors.locationCode ? "border-destructive" : ""}
            />
            {errors.locationCode && <p className="text-sm text-destructive">{errors.locationCode}</p>}
            {!isEditMode && <p className="text-xs text-muted-foreground">
              Auto-generated from hierarchy or enter a custom code
            </p>}
          </div>

          {/* Location Type */}
          <div className="space-y-2">
            <Label htmlFor="locationType">
              Location Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.locationType}
              onValueChange={(value) => setFormData({ ...formData, locationType: value as Location["locationType"] })}
            >
              <SelectTrigger className={errors.locationType ? "border-destructive" : ""}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Storage">Storage</SelectItem>
                <SelectItem value="Picking">Picking</SelectItem>
                <SelectItem value="Receiving">Receiving</SelectItem>
                <SelectItem value="Staging">Staging</SelectItem>
                <SelectItem value="Shipping">Shipping</SelectItem>
                <SelectItem value="Quarantine">Quarantine</SelectItem>
              </SelectContent>
            </Select>
            {errors.locationType && <p className="text-sm text-destructive">{errors.locationType}</p>}
          </div>

          {/* Picking Sequence */}
          <div className="space-y-2">
            <Label htmlFor="pickingSequence">
              Picking Sequence
            </Label>
            <Input
              id="pickingSequence"
              type="number"
              value={formData.pickingSequence || ""}
              onChange={(e) => setFormData({ ...formData, pickingSequence: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="Optional"
              min="0"
              className={errors.pickingSequence ? "border-destructive" : ""}
            />
            {errors.pickingSequence && <p className="text-sm text-destructive">{errors.pickingSequence}</p>}
            <p className="text-xs text-muted-foreground">
              Order in which this location should be picked (lower numbers picked first). Leave empty for no specific order.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
